import { get } from '../api'
import { isoDate } from './network/format'

/*
 * The node license of the installation (PLAT-LIC-1, docs/platform/licensing.md):
 * GET /api/admin/license (docs/controlapi.yaml LicenseView) and the texts of §6.3.
 */

export type LicenseState = 'none' | 'active' | 'expiring' | 'grace' | 'restricted' | 'service'
export type LicenseReason = 'no_license' | 'expired' | 'not_yet_valid' | 'fingerprint' | 'admin_domain' | 'updates' | 'activation_unavailable'
export type Seat = 'licensed' | 'waiting' | 'standby' | 'none'

export interface LicenseInfo {
  license_id: string
  customer: string
  type: 'trial' | 'pilot' | 'monthly' | 'annual' | 'hourly' | 'perpetual' | 'service'
  max_nodes: number
  captions: 'none' | 'small' | 'medium' | 'unlimited'
  overage: 'none' | 'hourly'
  issued_at: string
  not_before: string
  expires_at?: string
  grace_days: number
  updates_until?: string
  activation: 'offline' | 'online'
  admin_domain?: string
  support?: string
  note?: string
}

export interface LicenseStatus {
  state: LicenseState
  reason?: LicenseReason
  seats: number
  captions_workers: number
  new_seats: boolean
  new_streams: boolean
  expires_at?: string
  grace_until?: string
  days_left?: number
}

export interface LicenseNode {
  id: string
  name: string
  roles: string[]
  seat: Seat
  online: boolean
  minutes: number
  counted: boolean
}

export interface LicenseView {
  mode: 'observe' | 'enforce'
  dev_build?: boolean
  fingerprint: string
  admin_domain?: string
  source?: 'console' | 'file'
  license?: LicenseInfo
  load_errors?: string[]
  status: LicenseStatus
  clock: { watermark: string; behind: boolean }
  seats: { limit: number; unlimited: boolean; used: number; waiting: number; standby: number }
  usage: { month: string; peak: number; node_hours: number; days: { day: string; max_nodes: number }[] }
  nodes: LicenseNode[]
  captions: { limit: number; connected: number; licensed: number[]; minutes: number }
  refusals: { would_refuse_streams: number; refused_streams: number; seats_refused: number }
}

export const LICENSE_PATH = '/admin/license'
export const licenseKey = (month = '') => ['admin-license', month] as const
export const fetchLicense = (month = '') => get<LicenseView>(`/api/admin/license${month ? `?month=${encodeURIComponent(month)}` : ''}`)

export const TYPE_LABEL: Record<LicenseInfo['type'], string> = {
  trial: 'Trial', pilot: 'Pilot', monthly: 'Monthly', annual: 'Annual', hourly: 'Hourly', perpetual: 'Perpetual', service: 'Service',
}

export const CAPTIONS_LABEL: Record<LicenseInfo['captions'], string> = {
  none: 'None', small: 'Small · up to 3 workers', medium: 'Medium · up to 5 workers', unlimited: 'Unlimited',
}

const day = (iso?: string) => (iso ? isoDate(iso) : '')
export const licenseDay = day

/** licensing.md §6.3 reason.* */
export function reasonText(s: LicenseStatus, l?: LicenseInfo): string {
  switch (s.reason) {
    case 'expired':
      return `the license expired on ${day(s.expires_at ?? l?.expires_at)}`
    case 'fingerprint':
      return 'the key belongs to another installation'
    case 'admin_domain':
      return 'the key is bound to another admin domain'
    case 'updates':
      return `this version is newer than your updates period (until ${l?.updates_until ?? '—'})`
    case 'not_yet_valid':
      return `the license starts on ${day(l?.not_before)}`
    case 'activation_unavailable':
      return 'online activation is not available in this version'
    case 'no_license':
      return 'no license'
    default:
      return ''
  }
}

/** The state as a badge: glyph and word, readable without colour. */
export function stateLook(s: LicenseStatus): { glyph: string; word: string; tone: 'live' | 'warn' | 'fail' | 'muted' } {
  switch (s.state) {
    case 'active':
      return { glyph: '●', word: 'Active', tone: 'live' }
    case 'service':
      return { glyph: '●', word: 'Service license', tone: 'live' }
    case 'expiring':
      return { glyph: '▲', word: `Expires in ${s.days_left ?? 0} ${s.days_left === 1 ? 'day' : 'days'}`, tone: 'warn' }
    case 'grace':
      return { glyph: '▲', word: 'Expired · grace period', tone: 'warn' }
    case 'restricted':
      return { glyph: '✕', word: 'Inactive', tone: 'fail' }
    default:
      return { glyph: '○', word: 'No license', tone: 'muted' }
  }
}

/**
 * The banner every admin page shows (§4.3, §6.3), or null. A missing license
 * while the center only observes is not shouted on every page (our own stand
 * runs like that until its service key): the License page says it.
 */
export function bannerOf(v: LicenseView): { tone: 'warn' | 'fail'; title: string; text: string } | null {
  const s = v.status
  const l = v.license
  if (s.state === 'expiring' && (s.days_left ?? 99) <= 14) {
    return { tone: 'warn', title: `LICENSE EXPIRES IN ${s.days_left} ${s.days_left === 1 ? 'DAY' : 'DAYS'}`, text: `On ${day(s.expires_at)}. Load a renewed key on the License page.` }
  }
  if (s.state === 'grace') {
    return {
      tone: 'warn',
      title: 'LICENSE EXPIRED',
      text: `License expired on ${day(s.expires_at)}. New nodes can't get a seat. On ${day(s.grace_until)} new streams will stop being accepted; running streams are not affected.`,
    }
  }
  if (s.state === 'restricted') {
    return { tone: 'fail', title: 'LICENSE INACTIVE', text: `License inactive: ${reasonText(s, l)}. Running streams continue, new streams are refused.` }
  }
  if (s.state === 'none' && v.mode === 'enforce') {
    return { tone: 'fail', title: 'NO LICENSE', text: 'No license. Paste a license key on the License page.' }
  }
  if (v.seats.waiting > 0 && s.new_seats) {
    const n = v.seats.waiting
    return { tone: 'warn', title: 'NODES WITHOUT A SEAT', text: `${n} ${n === 1 ? 'node is' : 'nodes are'} over the license limit. ${n === 1 ? 'It takes' : 'They take'} no new streams until a seat is free.` }
  }
  return null
}

/**
 * What a pasted license line says, read in the browser WITHOUT checking the
 * signature (the center checks it on Apply): only for the preview.
 */
export function peekLicense(line: string): LicenseInfo | null {
  const s = line.trim()
  if (!s.startsWith('STREVIX-LIC-1.')) return null
  const parts = s.slice('STREVIX-LIC-1.'.length).split('.')
  if (parts.length !== 2 || !parts[0]) return null
  try {
    const b64 = parts[0].replace(/-/g, '+').replace(/_/g, '/')
    const bin = atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4))
    const json = new TextDecoder().decode(Uint8Array.from(bin, (c) => c.charCodeAt(0)))
    const p = JSON.parse(json) as Record<string, unknown> & { customer?: { name?: string }; install?: { admin_domain?: string } }
    if (typeof p.license_id !== 'string' || typeof p.type !== 'string') return null
    return {
      license_id: p.license_id,
      customer: p.customer?.name ?? '',
      type: p.type as LicenseInfo['type'],
      max_nodes: Number(p.max_nodes ?? 0),
      captions: (p.captions as LicenseInfo['captions']) ?? 'none',
      overage: (p.overage as LicenseInfo['overage']) ?? 'none',
      issued_at: String(p.issued_at ?? ''),
      not_before: String(p.not_before ?? ''),
      expires_at: typeof p.expires_at === 'string' ? p.expires_at : undefined,
      grace_days: Number(p.grace_days ?? 0),
      updates_until: typeof p.updates_until === 'string' ? p.updates_until : undefined,
      activation: (p.activation as LicenseInfo['activation']) ?? 'offline',
      admin_domain: p.install?.admin_domain,
    }
  } catch {
    return null
  }
}

/** The center's refusal of PUT /api/admin/license, in words. */
export function loadErrorText(code?: string, fallback = 'The license was not loaded.'): string {
  switch (code) {
    case 'license_format':
      return 'This is not a Strevix license. Paste the whole line that starts with STREVIX-LIC-1.'
    case 'license_signature':
      return 'The license is not signed by Strevix (or this version does not know its key).'
    case 'license_invalid':
      return 'The license content is invalid.'
    case 'license_fingerprint':
      return 'This key was issued for another installation. Send the fingerprint below to Strevix to get one for this center.'
    case 'license_admin_domain':
      return 'This key is bound to another admin domain.'
    case 'license_updates':
      return 'This key does not cover this version (its updates period ended before this release).'
    case 'license_activation_unavailable':
      return 'This key needs online activation, which this version does not have yet. Ask Strevix for an offline key.'
    default:
      return fallback
  }
}

/** Months for the usage selector: this one and the 12 before (UTC). */
export function recentMonths(now = new Date(), n = 13): string[] {
  const out: string[] = []
  for (let i = 0; i < n; i++) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1))
    out.push(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`)
  }
  return out
}
